import { Bank } from './Bank';
import { Guarantor } from './Guarantor';
import { CreditHistory } from './CreditHistory';
import { CreditTransaction } from './CreditTransaction';
import { Document } from './Document';
import { User } from './User';
import { Crop } from './Crop';
import { CreditCrop } from './CreditCrop';
import { CreditInsurance } from './CreditInsurance';
import { AirtableCredit } from './AirtableCredit';
import { CreditFarm } from './CreditFarm';
import { CreditCall } from './CreditCall';
import { CreditInvoice } from './CreditInvoice';
import { CreditMoney } from './CreditMoney';
import { Order } from './Order';
import { Chat } from './Chat';
import { CreditReport, CreditRisk, ReplyQuestion } from './CreditRisk';
import { DocumentFile } from './DocumentFile';
import { Contract } from './Contract';
import { CreditFileStatusTypes } from '@network';
import { AdminComment, AdminSettingsOption, CxCredit } from '@types';

export type CreditParDelay = '0' | 'Default' | '90' | '60' | '30';

export interface CollectionManager {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

export interface Credit {
  accredited: string | null | CreditAccredited;
  accreditedName: string | null;
  airtableCredits?: AirtableCredit[];
  airtableFields: Record<string, any>;
  airtableSyncAt: string | null;
  airtableUpdatedAt: string | null;
  balance: number;
  balanceTitle: string;
  bank: Bank | null;
  bankApproval: number;
  calls?: CreditCall[];
  chat?: Chat;
  code: string;
  collectionsManager: CollectionManager | null;
  collectionsManagerId: number | null;
  comments: AdminComment[];
  contactNextCycle: string | null;
  contract?: Contract;
  contractStatus: string | null;
  createdAt: string;
  createdByDealerId: string | null;
  creditCrops: CreditCrop[];
  creditFee: number;
  creditInsurances: CreditInsurance[];
  cropProblems: string;
  delayDays: number | null;
  docsLink: string;
  documents: Document[];
  exchangeRate: number | null;
  farmerDeposit: number;
  farms?: CreditFarm[];
  farmsCreditAmount: number;
  fileStatus: CreditFileStatusTypes | null;
  financedCrop: Crop | null;
  financedCropId: number | null;
  financedHa: number | null;
  guarantors: Guarantor[];
  history: CreditHistory[];
  hoursAnalysisResult: number | null;
  hoursCreatedAnalysis: number | null;
  id: number;
  insurance: number;
  insuranceCompany: string | null;
  insurances?: CreditInsurance[];
  interest: number;
  interestRate?: number;
  interestRateLoan?: number;
  invoices?: CreditInvoice[];
  isAdminDraft?: boolean;
  isDenied?: boolean;
  isGuarantorsRequired?: boolean;
  isManualBalance: boolean;
  isMoney: boolean;
  isUsd: boolean;
  manual: number;
  membership: number;
  membershipCompanyPercent: number;
  membershipPercent: number;
  modificationAt: string | null;
  money?: CreditMoney[];
  moratoryInterestRate?: number;
  moratoryInterestRateLoan?: number;
  nbfiStatus: string | CreditNbfiStatus;
  orders?: Order[];
  ordersAmount: number;
  ordersTotalAmount: number;
  parDelayDays: number;
  parDelayEdited?: boolean;
  parDelayFixed: CreditParDelay;
  paymentAmount: number;
  paymentAt: string | null;
  progressAt: string | null;
  progressPercent?: number;
  ptCapitalBalance: number | null;
  rating: number;
  refunds: number;
  rejectReason: string | null;
  rejectReasonId: number | null;
  rejectReasonObj: AdminSettingsOption | null;
  repayment: number;
  reports?: CreditReport[];
  requestAmount: number;
  requestContract: boolean;
  responsibleAdmin?: any;
  retention: number;
  risk?: CreditRisk;
  riskRejectReason?: string | null;
  riskRejectReasonId?: number | null;
  riskRejectReasonObj?: AdminSettingsOption | null;
  riskReplies?: ReplyQuestion[];
  riskScore: number | null;
  sfOpportunityLink: string | null;
  signatureAt: string | null;
  signedContractAt: string | null;
  status: CreditStatus;
  statusEn: string;
  timeApplyingToSignature?: string | null;
  timeApprovalToContractGen?: string | null;
  timeInAnalysis?: string | null;
  timeInApplying?: string | null;
  timeInApproved?: string | null;
  timeInCompletedFile?: string | null;
  timeInContractCreation?: string | null;
  timeInContractInProcess?: string | null;
  timeInContractSending?: string | null;
  timeInDelay?: string | null;
  timeInDocCollection?: string | null;
  timeInInNegotiations?: string | null;
  timeInKycScreening?: string | null;
  timeInPostScreening?: string | null;
  timeInPreScreening?: string | null;
  timeInSignature?: string | null;
  timeOfFarmerFile?: string | null;
  timerAnalysisToApprovedOrDeniedText?: string | null;
  timerCreatedToAnalysisText?: string | null;
  transactions: CreditTransaction[];
  usd_balance: number;
  usd_bank_approval: number;
  usd_credit_fee: number;
  usd_farmer_deposit: number;
  usd_finalPaymentAmount: number;
  usd_insurance: number;
  usd_interest: number;
  usd_membership: number;
  usd_ordersTotalAmount: number;
  usd_paymentAmount: number;
  usd_pfFinalBalance: number | null;
  usd_ptCapitalBalance: number | null;
  usd_ptFinalBalance: number | null;
  usd_refunds: number;
  usd_repayment: number;
  usd_requestAmount: number | null;
  usd_retention: number;
  user: User;
  userId: number;
}

export interface CreditReportFile extends File {
  id: number;
  file: DocumentFile;
}

export enum CreditStatus {
  DRAFT = 'draft',
  MODERATION = 'moderation',
  PRE_SCREENING = 'pre_screening',
  DOC_COLLECTION = 'doc_collection',
  KYC_SCREENING = 'kyc_screening',
  POST_SCREENING = 'post_screening',
  COMPLETED_FILE = 'completed_file',
  BANK_REVISION = 'bank_revision',
  APPROVED = 'approved',
  IN_NEGOTIATIONS = 'in_negotiations',
  CONTRACT_IN_PROCESS = 'contract_in_process',
  SIGNED = 'signed',
  REPAID = 'repaid',
  RESTRUCTURE = 'restructure',
  DELAY = 'delay',
  DENIED = 'denied',
}

export const creditStatusMap = {
  [CreditStatus.DRAFT]: 'Draft',
  [CreditStatus.MODERATION]: 'Applying',
  [CreditStatus.PRE_SCREENING]: 'Pre Screening',
  [CreditStatus.DOC_COLLECTION]: 'Doc Collection',
  [CreditStatus.KYC_SCREENING]: 'KYC Screening',
  [CreditStatus.POST_SCREENING]: 'Post Screening',
  [CreditStatus.COMPLETED_FILE]: 'Completed File',
  [CreditStatus.BANK_REVISION]: 'Analysis',
  [CreditStatus.APPROVED]: 'Approved',
  [CreditStatus.IN_NEGOTIATIONS]: 'In Negotiations',
  [CreditStatus.CONTRACT_IN_PROCESS]: 'Contract In Process',
  [CreditStatus.DENIED]: 'Denied',
  [CreditStatus.SIGNED]: 'Signed',
  [CreditStatus.REPAID]: 'Repaid',
  [CreditStatus.RESTRUCTURE]: 'Restructure',
  [CreditStatus.DELAY]: 'Delay',
};

export const creditFixedStatuses: CreditStatus[] = [
  CreditStatus.SIGNED,
  CreditStatus.APPROVED,
  CreditStatus.DELAY,
  CreditStatus.RESTRUCTURE,
];

export const creditFixedTitle = creditFixedStatuses.map(s => creditStatusMap[s]).join(', ');

export enum CreditAccredited {
  INDIVIDUAL = 'individual',
  COMPANY = 'company',
}

export enum CreditNbfiStatus {
  PENDING = 'Pending',
  PRE_ANALYSIS = 'Pre-Analysis',
  PRE_APPROVED = 'Pre-Approved',
  ORIGINATED = 'Originated',
  REJECTED = 'Rejected',
}

export const nbfiStatuses: CreditNbfiStatus[] = [
  CreditNbfiStatus.PENDING,
  CreditNbfiStatus.PRE_ANALYSIS,
  CreditNbfiStatus.PRE_APPROVED,
  CreditNbfiStatus.ORIGINATED,
  CreditNbfiStatus.REJECTED,
];

export const creditNbfiStatusOptions = nbfiStatuses.map(s => ({ id: s, name: s }));
